import type { SpecContent } from "@szyos/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { api, errorMessage } from "../api";
import { EmptyState, useToast } from "../components/feedback";
import { StudioChat } from "../components/StudioChat";
import { Icon } from "../icons";
import { PromoteForm } from "./Ideas";

/** Briefing-Entwurf der Beraterin als lesbare Karte. */
export function SpecDraftView({ spec }: { spec: SpecContent }) {
  return (
    <div className="spec-draft">
      <h3>Briefing-Entwurf</h3>
      <dl>
        <dt>Problem</dt>
        <dd>{spec.problem}</dd>
        <dt>Für wen</dt>
        <dd>{spec.audience}</dd>
        <dt>Muss-Funktionen</dt>
        <dd>
          <ul>
            {spec.mustHaves.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </dd>
        {spec.nonGoals.length > 0 && (
          <>
            <dt>Nicht dabei</dt>
            <dd>
              <ul>
                {spec.nonGoals.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </dd>
          </>
        )}
        {spec.designNotes && (
          <>
            <dt>Gestaltung</dt>
            <dd>{spec.designNotes}</dd>
          </>
        )}
        <dt>Akzeptanzkriterien</dt>
        <dd>
          <ul>
            {spec.acceptance.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </dd>
      </dl>
    </div>
  );
}

/** Planungsstudio für eine Idee: Klärung mit der Beraterin bis zum Briefing-Entwurf (M5-02). */
export function IdeaStudio({ ideaId }: { ideaId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [promoting, setPromoting] = useState(false);
  const ideas = useQuery({ queryKey: ["ideas"], queryFn: api.ideas });
  const sessions = useQuery({
    queryKey: ["studio", "idea", ideaId],
    queryFn: () => api.studioSessions({ ideaId }),
    refetchInterval: (q) => (q.state.data?.[0]?.state === "waiting_ai" ? 3000 : false),
  });
  const start = useMutation({
    mutationFn: () => api.startClarification(ideaId),
    onSuccess: async () => {
      toast("Klärung gestartet. Die Beraterin liest deine Idee.");
      await queryClient.invalidateQueries({ queryKey: ["studio"] });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const idea = ideas.data?.find((i) => i.id === ideaId);
  const session = sessions.data?.[0];
  const draft = session?.proposal as SpecContent | null | undefined;

  if (ideas.isSuccess && !idea) return <p className="form-error">Idee nicht gefunden.</p>;
  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <p className="eyebrow">
            <Link to="/ideas" className="link">
              Ideen
            </Link>
            <span className="chip chip--ghost">Planungsstudio</span>
          </p>
          <h1>{idea?.title ?? "…"}</h1>
          {idea?.body && <p className="hero-sub">{idea.body}</p>}
        </div>
      </section>
      {!session && (
        <section className="panel studio-intro">
          <EmptyState
            icon="bot"
            title="Idee mit der Beraterin ausarbeiten"
            text="Sie fasst deine Idee zusammen und stellt dir die wichtigsten Rückfragen. Aus deinen Antworten entsteht ein Briefing-Entwurf, den du vor dem Programmieren prüfst. Jede Antwort ist ein kurzer Claude-Lauf über dein Abo."
          />
          <button
            type="button"
            className="btn btn--primary"
            disabled={start.isPending}
            onClick={() => start.mutate()}
          >
            <span className="btn-label">
              <Icon name="bot" /> Klärung starten
            </span>
          </button>
        </section>
      )}
      {session && (
        <StudioChat
          session={session}
          title="Klärung mit der Beraterin"
          onRetry={() => start.mutate()}
          proposalView={
            draft ? (
              <div className="studio-proposal">
                <SpecDraftView spec={draft} />
                <p className="muted">
                  Passt der Entwurf, mach ein Projekt daraus. Im Auftrag übernimmst du ihn dann ins Briefing
                  und kannst alles noch anpassen.
                </p>
                {idea && (
                  <button
                    type="button"
                    className="btn btn--primary"
                    aria-expanded={promoting}
                    onClick={() => setPromoting((p) => !p)}
                  >
                    <span className="btn-label">
                      <Icon name="folder" /> Zu Projekt machen
                    </span>
                  </button>
                )}
                {promoting && idea && <PromoteForm idea={idea} onDone={() => setPromoting(false)} />}
              </div>
            ) : undefined
          }
        />
      )}
    </>
  );
}
